import { useState, useEffect, useCallback } from "react";
import type { TournamentFormState } from "@/api/tournaments";

export interface TournamentValidationErrors {
  nameDescription: { hasError: boolean; message: string };
  audience: { hasError: boolean; message: string };
  content: { hasError: boolean; message: string };
  settings: {
    startDate: { hasError: boolean; message: string };
    endDate: { hasError: boolean; message: string };
    contactPersons: { hasError: boolean; message: string };
  };
}

const DEFAULT_ERRORS: TournamentValidationErrors = {
  nameDescription: { hasError: true, message: "Please provide a name and description" },
  audience: { hasError: true, message: "Please add at least one user" },
  content: { hasError: true, message: "Please select at least one lesson" },
  settings: {
    startDate: { hasError: true, message: "Start date is required" },
    endDate: { hasError: true, message: "End date is required" },
    contactPersons: { hasError: true, message: "Contact info is required" },
  },
};

const TOTAL_STEPS_COUNT = 5;

export function useTournamentValidation(currentStep: number): {
  validationErrors: TournamentValidationErrors;
  isFormValid: boolean;
  showValidationMessages: boolean;
  validateSection: (
    section: keyof TournamentFormState,
    values: TournamentFormState[keyof TournamentFormState]
  ) => void;
} {
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<TournamentValidationErrors>(DEFAULT_ERRORS);

  useEffect(() => {
    if (currentStep === TOTAL_STEPS_COUNT - 1) {
      setShowValidationMessages(true);
    }
  }, [currentStep]);

  const validateSection = useCallback(
    (
      section: keyof TournamentFormState,
      values: TournamentFormState[keyof TournamentFormState]
    ) => {
      switch (section) {
        case "basicInfo": {
          const v = values as TournamentFormState["basicInfo"];
          const nameError = !v.name || v.name.trim().length === 0;
          const descError = !v.description || v.description.trim().length === 0;
          setValidationErrors(prev => ({
            ...prev,
            nameDescription: {
              hasError: nameError || descError,
              message: nameError
                ? "Tournament name is required"
                : descError
                  ? "Tournament description is required"
                  : "",
            },
          }));
          break;
        }
        case "audience": {
          const v = values as TournamentFormState["audience"];
          if (v.isGlobal) {
            setValidationErrors(prev => ({
              ...prev,
              audience: { hasError: false, message: "" },
            }));
            break;
          }
          setValidationErrors(prev => ({
            ...prev,
            audience: {
              hasError: (v.users ?? []).length === 0,
              message: "Please add at least one user",
            },
          }));
          break;
        }
        case "content": {
          const v = values as TournamentFormState["content"];
          setValidationErrors(prev => ({
            ...prev,
            content: {
              hasError: (v.selected ?? []).length === 0,
              message: "Please select at least one lesson",
            },
          }));
          break;
        }
        case "settings": {
          const v = values as TournamentFormState["settings"];
          setValidationErrors(prev => ({
            ...prev,
            settings: {
              startDate: { hasError: !v.startDate, message: "Start date is required" },
              endDate: { hasError: !v.endDate, message: "End date is required" },
              contactPersons: {
                hasError: !v.contactPersons || v.contactPersons.trim().length === 0,
                message: "Contact info is required",
              },
            },
          }));
          break;
        }
        default:
          break;
      }
    },
    []
  );

  const isFormValid =
    !validationErrors.nameDescription.hasError &&
    !validationErrors.audience.hasError &&
    !validationErrors.content.hasError &&
    !validationErrors.settings.startDate.hasError &&
    !validationErrors.settings.endDate.hasError &&
    !validationErrors.settings.contactPersons.hasError;

  return { validationErrors, isFormValid, showValidationMessages, validateSection };
}
